/**
 * Unified Permission Service
 *
 * Consolidates feature-based and role-based permission systems into a single
 * source of truth. Uses database-driven configuration with caching and audit logging.
 *
 * Permission Resolution Order:
 * 1. User-specific override (highest priority)
 * 2. Feature permission (from user_type)
 * 3. Role permission (from role_permissions table)
 * 4. Route permission (for route access)
 * 5. Default: DENY (fail closed)
 */

import { supabase } from '../lib/supabase';

// ============================================
// Types
// ============================================

export type Role = 'super_admin' | 'admin' | 'manager' | 'agent' | 'viewer' | 'customer';

export interface UnifiedPermission {
  hasAccess: boolean;
  source: 'override' | 'user_type' | 'role' | 'route' | 'approval' | 'default';
  config?: Record<string, unknown>;
  deniedReason?: string;
}

export interface RolePermission {
  id: string;
  role: string;
  resource: string;
  action: string;
  granted: boolean;
  description?: string;
}

export interface RoutePermission {
  id: string;
  route_path: string;
  route_name?: string;
  minimum_role?: Role;
  required_features?: string[];
  required_permissions?: [string, string][];
  is_public: boolean;
  is_active: boolean;
}

export const NEW_TICKET_CANONICAL_ROUTE = '/ucc/new';
export const NEW_TICKET_ALIAS_ROUTE = '/communications/new';

/**
 * Routes mounted outside an authenticated route guard. Database configuration
 * cannot make another browser route public: changes to this list require a
 * reviewed source change and a matching router-inventory test update.
 */
export const PUBLIC_ROUTE_PATTERNS = Object.freeze([
  '/login',
  '/register',
  '/auth/magic',
  '/itinerary/:token',
  '/proposal/:locator',
  '/statement/:token',
  '/trip-status',
  '/booking',
  '/booking/:token',
  '/pay/:token',
  '/itinerary/view/:id',
] as const);

/**
 * Exact inventory of routes mounted below ProtectedRoute or
 * PropellerProtectedRoute. Dynamic segments are declared with `:name`.
 * Anything outside this inventory is denied before role/database evaluation.
 */
export const AUTHENTICATED_ROUTE_PATTERNS = Object.freeze([
  '/',
  '/dashboard',
  '/dashboard/locations',
  '/ai-assistant',
  '/ai-hub',
  '/ai/sabre-agent',
  '/ai/amadeus-agent',
  '/ai/threads',
  '/ai/pnr-parser',
  '/ai/pnr-agent',
  '/ai/stats',
  '/ai/proplets',
  '/ai/proplets/chat',
  '/ai/agent-console',
  '/ai/supplier-search',
  '/ai/vms',
  '/agent/live/:sessionId',
  '/agent/live',
  '/search',
  '/search/flights',
  '/search/advanced',
  '/consolidators',
  '/consolidators/search',
  '/quick-book',
  '/price-comparison',
  '/air/itinerary-composer',
  '/air/quote-research',
  '/air/exchange-studio',
  '/bookings',
  '/bookings/deadlines',
  '/pnr',
  '/pnr/new',
  '/pnr/record/:id',
  '/pnr/:slug',
  '/hotels',
  '/hotels/search',
  '/hotels/ratehawk',
  '/hotels/bookings',
  '/hotels/ratehawk/bookings',
  '/hotels/ratehawk/payments',
  '/hotels/:hotelBookingId/confirmation',
  '/hotels/confirmation',
  '/hotels/automation',
  '/hotels/nights',
  '/cars',
  '/cars/search',
  '/duffel',
  '/duffel/search',
  '/sabre',
  '/sabre/availability',
  '/sabre/pnr-create',
  '/sabre/fare-rules',
  '/sabre/ancillary',
  '/sabre/queue-automation',
  '/sabre/cryptic',
  '/sabre/destination-finder',
  '/sabre/pnr-split',
  '/queue-center',
  '/queue-center/print',
  '/manuals',
  '/automation/queues',
  '/automation/queue-actions',
  '/automation/queue-report',
  '/automation/queue-config',
  '/automation/queue-rules',
  '/automation/schedule-changes',
  '/automation/ttl',
  '/automation/ticketing',
  '/amadeus/queue-manager',
  '/amadeus/terminal',
  '/amadeus/availability',
  '/amadeus/pnr-create',
  '/amadeus/fare-rules',
  '/amadeus/ancillary',
  '/automation/checkin',
  '/automation',
  '/amadeus/agent',
  '/checkin',
  '/rtw',
  '/rtw/assistant',
  '/rtw/troubleshoot',
  '/rtw/agent/:ticketId',
  '/rtw/fab-intel-map',
  '/rtw/shared-links',
  '/rtw/invite-drafts',
  '/rtw/desk-queue',
  '/rtw/fulfilment-review',
  '/ai-inbox',
  '/ai-inbox/:draftId/console',
  '/admin/terms-templates',
  '/loyalty',
  '/loyalty/calc',
  '/tier-points-calculator',
  '/crm',
  '/crm/customers',
  '/customers',
  '/crm/customers/:id',
  '/customers/:id',
  '/crm/customers/:id/360',
  '/customers/:id/360',
  '/crm/companies',
  '/companies',
  '/crm/companies/:id',
  '/companies/:id',
  '/companies/:id/edit',
  '/crm/travelers',
  '/travelers',
  '/crm/travelers/:id',
  '/travelers/:id',
  '/crm/person/:id',
  '/crm/suppliers',
  '/crm/suppliers/:id',
  '/crm/providers',
  '/suppliers',
  '/crm/leads',
  '/crm/leads/:id',
  '/leads',
  '/crm/communication-logs',
  '/crm/suggested-updates',
  '/crm/contact-matching',
  '/crm/whatsapp-notes',
  '/communication-logs',
  '/communication-hub',
  '/crm/documents',
  '/documents',
  '/crm/documents/manager',
  '/documents/manager',
  '/crm/documents/acknowledgements',
  '/documents/acknowledgements',
  '/tasks',
  '/action-plans',
  '/routines',
  '/finance',
  '/invoices',
  '/invoices/templates',
  '/invoices/new',
  '/invoices/:invoiceId/builder',
  '/payments',
  '/finance/payment-sources',
  '/finance/stripe-balance',
  '/finance/stripe-settlement',
  '/finance/payment-economics',
  '/finance/stripe-customers',
  '/finance/settlements',
  '/finance/terminal',
  '/finance/moto',
  '/finance/card-vault-fop',
  '/finance/refunds',
  '/finance/aging',
  '/finance/cashflow',
  '/payments/reconciliation',
  '/commissions',
  '/banking',
  '/banking/categorize',
  '/banking/rules',
  '/banking/categories',
  '/banking/analytics',
  '/banking/resolutions',
  '/banking/reconcile',
  '/banking/plaid-oauth',
  '/finance/supplier-invoices',
  '/finance/statement-reconciliation',
  '/finance/supplier-credit',
  '/finance/supplier-config',
  '/finance/commission-engine',
  '/finance/chart-of-accounts',
  '/finance/journal-entries',
  '/revenue',
  '/revenue/dashboard',
  '/reports',
  '/reports/:category',
  '/reports/dashboard',
  '/reports/builder',
  '/finance/fora',
  '/risk',
  '/risk/dashboard',
  '/alerts',
  '/alerts/flights',
  '/expenses',
  '/expenses/manager',
  '/compliance',
  '/compliance/audit',
  '/inbox',
  '/ops/inbox',
  '/ops/reply-queue',
  '/ops/today',
  '/ops/waiting',
  '/ops/lift-and-shift',
  '/calls',
  '/calls/health',
  '/ops/identity-review',
  '/ops/dispatch',
  '/ops/control-tower',
  '/ops/handbook',
  '/ops/customer-requests',
  '/ops/stale-work',
  '/operations/queue',
  '/operations/promises-and-passports',
  '/itinerary/builder',
  '/itinerary/advanced',
  '/proposals',
  '/proposals/tracking',
  '/pnr/history',
  '/pnt',
  '/pnt/obligations',
  '/pnt/plans',
  '/pnt/analytics',
  '/pnt/outlook',
  '/pnt/digest-settings',
  '/interface-import',
  '/interface-files',
  '/sabre/terminal',
  '/sabre/diagnostics',
  '/trips',
  '/trips/:id',
  '/trips/:id/edit',
  '/trips/:id/statement',
  '/partners',
  '/partners/new',
  '/partners/payouts',
  '/partners/:id',
  '/partners/:id/commission/new',
  '/partners/:id/commission/:modelId',
  '/partners/:id/permissions',
  '/ucc',
  '/communications',
  '/freshdesk',
  '/freshdesk/sync',
  '/freshdesk/analyze',
  '/documents/generate',
  '/settings/gds',
  '/settings',
  '/settings/personas',
  '/admin/personas',
  '/ai-manual',
  '/admin/ai-manual',
  '/admin/manual',
  '/manuals/team-guide',
  '/settings/connections',
  '/settings/sabre-agent',
  '/settings/communications',
  '/settings/automations',
  '/settings/api-access',
  '/settings/next-work-digest',
  '/settings/morning-message',
  '/settings/schedule-changes',
  '/analytics',
  '/ai',
  '/ai/hub',
  '/amadeus',
  '/ops',
  '/itinerary',
  '/automations',
  '/cases',
  '/cases/:displayId',
  '/air',
  '/deadlines',
  '/hotels/quotes',
  '/api-docs',
  '/automations/fora-sync',
  '/crm/hotels',
  '/integrations',
  '/integrations/todoist',
  '/integrations/pipedream',
  '/integrations/:id',
  '/admin',
  '/admin/portal-profiles',
  '/admin/users',
  '/admin/approvals',
  '/admin/permissions',
  '/admin/email-templates',
  '/admin/loyalty-programs',
  '/admin/perk-programs',
  '/finance/payment-methods',
  '/finance/client-charges',
  '/finance/payment-links',
  '/admin/earning-rules',
  '/admin/earning-charts',
  '/admin/pnr-taxonomy',
  '/admin/calculation-tester',
  '/admin/audit-log',
  '/admin/rbac',
  '/admin/web-scraper',
  '/admin/login-cms',
  '/admin/airline-classes',
  '/admin/pbx-routing',
  '/admin/pax-banners',
  '/admin/pax-content',
  '/admin/pax-invite',
  '/admin/sla-dashboard',
  '/admin/system-health',
  '/admin/system-manual',
  '/admin/fd-import',
  '/admin/crm-tags',
  NEW_TICKET_CANONICAL_ROUTE,
  '/ucc/:displayId',
  NEW_TICKET_ALIAS_ROUTE,
  '/communications/:displayId',
  '/analytics/agents',
  '/analytics/queue',
  '/data-quality',
  '/data-quality/orphans',
  '/templates',
  '/tasks/management',
  '/qc-checklists',
  '/quality-control',
  '/finance/ar-aging',
  '/finance/ap-management',
  '/suppliers/rates',
  '/analytics/agent-performance',
  '/trip-files',
  '/documents/trip-files',
  '/templates/documents',
  '/templates/emails',
  '/alerts/smart',
  '/smart-alerts',
  '/groups',
  '/bookings/groups',
  '/finance/service-fees',
  '/finance/payment-plans',
  '/finance/refund-management',
  '/revenue/analytics',
  '/crm/segments',
  '/customers/segments',
  '/crm/vip',
  '/vip-support',
  '/knowledge-base',
  '/admin/kb',
  '/aoc',
  '/operations-center',
  '/admin/pnr-rules',
  '/admin/booking-queue',
  '/admin/supplier-pollers',
  '/admin/platform-events',
  '/admin/automations',
  '/admin/delivery',
  '/admin/assistant-controls',
  '/admin/sabre-pool',
  '/admin/rtw-fare-rules',
  '/admin/gds-remark-review',
  '/admin/email-domain-repairs',
  '/admin/managed-lists',
  '/admin/workforce',
  '/ops/command-center',
  '/propeller',
  '/propeller/availability',
  '/propeller/fare-quote',
  '/propeller/price-explorer',
  '/propeller/seat-map',
  '/propeller/search',
  '/propeller/seat-maps',
  '/propeller/alerts',
  '/propeller/flifo',
  '/propeller/mct',
  '/propeller/award-search',
  '/propeller/profile',
  '/propeller/settings',
] as const);

export type ApplicationRoutePolicy = 'public' | 'authenticated' | 'deny';

const STATIC_MINIMUM_ROUTE_ROLES: Readonly<Record<string, Role>> = Object.freeze({
  [NEW_TICKET_CANONICAL_ROUTE]: 'agent',
  [NEW_TICKET_ALIAS_ROUTE]: 'agent',
  '/air/itinerary-composer': 'agent',
  '/air/quote-research': 'agent',
  '/air/exchange-studio': 'agent',
  '/finance/moto': 'agent',
  '/finance/card-vault-fop': 'agent',
  '/finance/payment-links': 'agent',
  // Ticketing deadlines desk (R4): approved staff only (the database reader also refuses anyone else).
  '/bookings/deadlines': 'agent',
  // Suggested updates queue (contact enrichment): administrators (the database refuses anyone else).
  '/crm/suggested-updates': 'admin',
  // Customer matching: approved staff (the database refuses everyone else and hides the administrator sections from non-administrators).
  '/crm/contact-matching': 'agent',
  // WhatsApp notes in Freshdesk requests: approved staff read and choose a request; the settings and put-aside actions are administrators only inside the database.
  '/crm/whatsapp-notes': 'agent',
  // Airline schedule changes (what the detector found, the last runs and the settings): approved staff read; the settings are administrators only inside the database.
  '/settings/schedule-changes': 'agent',
  '/settings/personas': 'agent',
  '/admin/personas': 'agent',
  '/ai-manual': 'agent',
  '/admin/ai-manual': 'agent',
  '/admin/manual': 'agent',
  '/manuals/team-guide': 'agent',
  // Parent breadcrumb redirects (17 Oct 2026, shell IA): /ops goes to Today for approved staff; /automations goes to the administrators' Automations page.
  '/ops': 'agent',
  '/automations': 'admin',
  '/cases': 'agent',
  '/cases/:displayId': 'agent',
  '/air': 'agent',
  '/deadlines': 'agent',
  '/hotels/quotes': 'agent',
  // Today: approved staff only (the database readers also refuse anyone else); the settings are administrators only inside the database.
  '/ops/today': 'agent',
  // Operations Command Center (1-click automation toggles + enriched unassigned intake): approved staff read and assign; automation toggle mutations require manager/admin inside the database.
  '/ops/command-center': 'agent',
  // Waiting for a reply: approved staff only (the database view also returns nothing to anyone else).
  '/ops/waiting': 'agent',
  // Lift and shift (bookings held on stand-in dates, the staff task, the draft message): approved staff only (every database function behind it refuses anyone else); the settings are administrators only inside the database.
  '/ops/lift-and-shift': 'agent',
  // Calls (every call, meeting and phone recording with its masked transcript and analysis): approved staff only (every database function behind it refuses anyone else); the settings are administrators only inside the database, and the staff-only and personal stubs are listed for administrators only.
  '/calls': 'agent',
  '/calls/health': 'agent',
  '/operations/promises-and-passports': 'agent',
  // Automations (what every scheduled job and outside task did on each run): administrators only (the three database readers refuse anyone else).
  '/admin/automations': 'admin',
  // Delivery and decisions (the build summary, the decision cards, the plan): administrators only (every database function behind it refuses anyone else).
  '/admin/delivery': 'admin',
  // Team AI Desk Assistant controls & quality signals: approved staff read; administrator changes enforced in database.
  '/admin/assistant-controls': 'agent',
  // Shared Sabre session pool & booking-refresh telemetry: approved staff read; administrator settings and lease recovery enforced in database.
  '/admin/sabre-pool': 'agent',
  // RTW filed fare rules (the rule text fetched from Sabre, its settings, Fetch now): administrators only for changes; the database functions behind it admit approved staff to read and administrators to change.
  '/admin/rtw-fare-rules': 'admin',
  // Workforce, Granular Subtask Routing Matrix & Payroll Admin Page: approved staff read skill profiles & routing rules; administrator settings for pay rates, Hubstaff config, and payroll enforced in database.
  '/admin/workforce': 'agent',
  // Hotel quote follow-ups, $5k+ call tasks, Flight/RTW cross-sell & loyalty rules: approved staff only (database functions refuse anyone else; control updates require administrator).
  '/hotels/automation': 'agent',
  '/hotels/nights': 'agent',
  '/crm/providers': 'agent',
  '/rtw/desk-queue': 'agent',
  '/rtw/fulfilment-review': 'agent',
});

export function normalizeRoutePath(routePath: string): string {
  const [pathOnly = '/'] = routePath.split(/[?#]/, 1);
  const withLeadingSlash = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  const normalized = withLeadingSlash.replace(/\/{2,}/g, '/').replace(/\/$/, '');
  return normalized || '/';
}

function routeMatchesPattern(routePath: string, routePattern: string): boolean {
  const routeSegments = normalizeRoutePath(routePath).split('/').filter(Boolean);
  const patternSegments = normalizeRoutePath(routePattern).split('/').filter(Boolean);

  if (routeSegments.length !== patternSegments.length) return false;

  return patternSegments.every((segment, index) => (
    segment.startsWith(':') || segment === routeSegments[index]
  ));
}

export function getApplicationRoutePolicy(routePath: string): ApplicationRoutePolicy {
  const normalizedPath = normalizeRoutePath(routePath);
  const isExactMatch = (pattern: string) => (
    !pattern.includes(':') && normalizeRoutePath(pattern) === normalizedPath
  );

  // Preserve router specificity: an explicit authenticated route such as
  // /itinerary/builder must not be exposed by the public /itinerary/:token
  // pattern. Authenticated wins any equally specific cross-policy collision.
  if (AUTHENTICATED_ROUTE_PATTERNS.some(isExactMatch)) {
    return 'authenticated';
  }
  if (PUBLIC_ROUTE_PATTERNS.some(isExactMatch)) {
    return 'public';
  }
  if (AUTHENTICATED_ROUTE_PATTERNS.some((pattern) => routeMatchesPattern(normalizedPath, pattern))) {
    return 'authenticated';
  }
  if (PUBLIC_ROUTE_PATTERNS.some((pattern) => routeMatchesPattern(normalizedPath, pattern))) {
    return 'public';
  }
  return 'deny';
}

export function getMinimumRoleForRoute(routePath: string): Role | undefined {
  return STATIC_MINIMUM_ROUTE_ROLES[normalizeRoutePath(routePath)];
}

export function roleSatisfiesMinimumRole(role: Role, minimumRole: Role): boolean {
  return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[minimumRole];
}

function isRole(value: unknown): value is Role {
  return typeof value === 'string' && value in ROLE_HIERARCHY;
}

/** @deprecated Use getApplicationRoutePolicy for the authoritative decision. */
export function isFailClosedRoute(routePath: string): boolean {
  return getApplicationRoutePolicy(routePath) !== 'public';
}

export const ROUTE_ACCESS_TIMEOUT_MS = 6000;

interface RouteAccessDependencies {
  checkRouteAccess: (userId: string, routePath: string) => Promise<UnifiedPermission>;
}

function deniedPermission(reason: string): UnifiedPermission {
  return {
    hasAccess: false,
    source: 'default',
    deniedReason: reason,
  };
}

export async function checkRouteAccessWithTimeout(
  userId: string,
  routePath: string,
  timeoutMs = ROUTE_ACCESS_TIMEOUT_MS,
  dependencies: RouteAccessDependencies = { checkRouteAccess },
): Promise<UnifiedPermission> {
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;

  try {
    return await Promise.race([
      dependencies.checkRouteAccess(userId, routePath)
        .catch(() => deniedPermission('Authorization check failed')),
      new Promise<UnifiedPermission>((resolve) => {
        timeoutId = globalThis.setTimeout(() => {
          resolve(deniedPermission('Authorization check timed out'));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
  }
}

export interface UserContext {
  userId: string;
  role: Role;
  userTypeId?: string;
  isApproved: boolean;
}

export interface SecurityEvent {
  eventType: string;
  userId?: string;
  details?: Record<string, unknown>;
  severity?: 'info' | 'warning' | 'critical';
  routePath?: string;
  resource?: string;
  action?: string;
}

// ============================================
// Role Hierarchy
// ============================================

const ROLE_HIERARCHY: Record<Role, number> = {
  super_admin: 100,
  admin: 80,
  manager: 60,
  agent: 40,
  viewer: 20,
  customer: 10,
};

// ============================================
// Caching
// ============================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const userContextCache = new Map<string, CacheEntry<UserContext>>();
// In-flight de-dup: a page can mount 100+ permission-gated elements that each
// call getUserContext in the same tick — before the cache fills. Without this,
// that stampede fired ~187 identical user_profiles requests per PNR page load
// and saturated the browser's connection pool (starving the record fetch).
const userContextInflight = new Map<string, Promise<UserContext | null>>();
const rolePermissionsCache: CacheEntry<RolePermission[]> | null = { data: [], timestamp: 0 };
const routePermissionsCache: CacheEntry<RoutePermission[]> | null = { data: [], timestamp: 0 };

const USER_CONTEXT_CACHE_TTL = 60000; // 1 minute
const ROLE_PERMISSIONS_CACHE_TTL = 300000; // 5 minutes (changes rarely)
const ROUTE_PERMISSIONS_CACHE_TTL = 300000; // 5 minutes

/**
 * Clear all caches or specific user cache
 */
export function clearUnifiedPermissionCache(userId?: string): void {
  if (userId) {
    userContextCache.delete(userId);
  } else {
    userContextCache.clear();
  }
}

/**
 * Clear role and route permission caches (admin action)
 */
export function clearGlobalPermissionCaches(): void {
  if (rolePermissionsCache) {
    rolePermissionsCache.data = [];
    rolePermissionsCache.timestamp = 0;
  }
  if (routePermissionsCache) {
    routePermissionsCache.data = [];
    routePermissionsCache.timestamp = 0;
  }
}

// ============================================
// User Context
// ============================================

/**
 * Get user context including role, user_type, and approval status
 */
export async function getUserContext(userId: string): Promise<UserContext | null> {
  // Fresh cache hit — return immediately.
  const cached = userContextCache.get(userId);
  if (cached && Date.now() - cached.timestamp < USER_CONTEXT_CACHE_TTL) {
    return cached.data;
  }

  // Coalesce concurrent cache-misses into a single request.
  const existing = userContextInflight.get(userId);
  if (existing) return existing;

  const promise = (async (): Promise<UserContext | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role, user_type_id, approval_status')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user context:', error);
        // A transport/PostgREST failure is not evidence that the user profile
        // does not exist. Let route guards retry it and apply their explicit
        // transient-error policy instead of converting it into a false
        // "User not found" denial.
        throw error;
      }

      // A successful maybeSingle() with no row is the only genuine
      // "User not found" result.
      if (!data) return null;

      if (!isRole(data.role)) {
        throw new Error('User profile contains an invalid role');
      }

      const role = data.role;

      // Admins and super_admins are always considered approved
      const isApproved =
        role === 'admin' ||
        role === 'super_admin' ||
        data.approval_status === 'approved';

      const context: UserContext = {
        userId,
        role,
        userTypeId: data.user_type_id,
        isApproved,
      };

      // Cache the context
      userContextCache.set(userId, { data: context, timestamp: Date.now() });

      return context;
    } catch (error) {
      console.error('Error getting user context:', error);
      throw error;
    } finally {
      userContextInflight.delete(userId);
    }
  })();

  userContextInflight.set(userId, promise);
  return promise;
}

// ============================================
// Role Permissions (from database)
// ============================================

/**
 * Get all role permissions from database
 */
export async function getRolePermissions(): Promise<RolePermission[]> {
  // Check cache
  if (
    rolePermissionsCache &&
    rolePermissionsCache.data.length > 0 &&
    Date.now() - rolePermissionsCache.timestamp < ROLE_PERMISSIONS_CACHE_TTL
  ) {
    return rolePermissionsCache.data;
  }

  try {
    const { data, error } = await supabase
      .from('role_permissions')
      .select('*')
      .eq('granted', true);

    if (error) {
      console.error('Error fetching role permissions:', error);
      return [];
    }

    const permissions = (data || []) as RolePermission[];

    // Update cache
    if (rolePermissionsCache) {
      rolePermissionsCache.data = permissions;
      rolePermissionsCache.timestamp = Date.now();
    }

    return permissions;
  } catch (error) {
    console.error('Error getting role permissions:', error);
    return [];
  }
}

/**
 * Check if a role has permission for resource:action
 */
export async function checkRolePermission(
  role: Role,
  resource: string,
  action: string
): Promise<boolean> {
  // Super admin always has access
  if (role === 'super_admin') return true;

  const permissions = await getRolePermissions();

  return permissions.some(p => {
    if (p.role !== role) return false;

    // Check for wildcards
    if (p.resource === '*' && p.action === '*') return true;
    if (p.resource === resource && p.action === '*') return true;
    if (p.resource === '*' && p.action === action) return true;
    if (p.resource === resource && p.action === action) return true;

    return false;
  });
}

// ============================================
// Route Permissions (from database)
// ============================================

/**
 * Get all route permissions from database
 */
export async function getRoutePermissions(): Promise<RoutePermission[]> {
  // Check cache
  if (
    routePermissionsCache &&
    routePermissionsCache.data.length > 0 &&
    Date.now() - routePermissionsCache.timestamp < ROUTE_PERMISSIONS_CACHE_TTL
  ) {
    return routePermissionsCache.data;
  }

  try {
    const { data, error } = await supabase
      .from('route_permissions')
      .select('*')
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching route permissions:', error);
      throw error;
    }

    const permissions = (data || []) as RoutePermission[];

    // Update cache
    if (routePermissionsCache) {
      routePermissionsCache.data = permissions;
      routePermissionsCache.timestamp = Date.now();
    }

    return permissions;
  } catch (error) {
    console.error('Error getting route permissions:', error);
    throw error;
  }
}

/**
 * Check if a user can access a specific route
 */
export async function checkRouteAccess(
  userId: string,
  routePath: string
): Promise<UnifiedPermission> {
  const normalizedRoutePath = normalizeRoutePath(routePath);
  const applicationPolicy = getApplicationRoutePolicy(normalizedRoutePath);

  if (applicationPolicy === 'public') {
    return { hasAccess: true, source: 'route' };
  }

  if (applicationPolicy === 'deny') {
    return {
      hasAccess: false,
      source: 'default',
      deniedReason: 'Route is not registered in application policy',
    };
  }

  const context = await getUserContext(userId);

  if (!context) {
    return {
      hasAccess: false,
      source: 'default',
      deniedReason: 'User not found',
    };
  }

  if (!context.isApproved) {
    return {
      hasAccess: false,
      source: 'approval',
      deniedReason: 'User not approved',
    };
  }

  // Super admin bypasses all checks
  if (context.role === 'super_admin') {
    return { hasAccess: true, source: 'role' };
  }

  const routePerms = await getRoutePermissions();

  // Exact route rules win. Otherwise use the longest matching wildcard so a
  // broad domain policy cannot shadow a more specific child policy.
  const exactRoutePermission = routePerms.find((permission) => (
    normalizeRoutePath(permission.route_path) === normalizedRoutePath
  ));
  const wildcardRoutePermission = routePerms
    .filter((permission) => permission.route_path.endsWith('/*'))
    .filter((permission) => {
      const basePath = normalizeRoutePath(permission.route_path.slice(0, -2));
      return normalizedRoutePath === basePath || normalizedRoutePath.startsWith(`${basePath}/`);
    })
    .sort((a, b) => b.route_path.length - a.route_path.length)[0];
  const routePerm = exactRoutePermission || wildcardRoutePermission;

  const sourceMinimumRole = getMinimumRoleForRoute(normalizedRoutePath);
  const databaseMinimumRole = routePerm?.minimum_role;

  if (databaseMinimumRole && !isRole(databaseMinimumRole)) {
    return {
      hasAccess: false,
      source: 'default',
      deniedReason: 'Route policy contains an invalid minimum role',
    };
  }

  const minimumRole = [sourceMinimumRole, databaseMinimumRole]
    .filter((role): role is Role => Boolean(role))
    .sort((a, b) => ROLE_HIERARCHY[b] - ROLE_HIERARCHY[a])[0];

  if (minimumRole && !roleSatisfiesMinimumRole(context.role, minimumRole)) {
    if (
      roleSatisfiesMinimumRole(context.role, 'agent') &&
      routePerm?.required_features &&
      routePerm.required_features.length > 0
    ) {
      const overrides = await Promise.all(
        routePerm.required_features.map((featureKey) => checkUserOverride(userId, featureKey))
      );
      if (overrides.every((ovr) => ovr !== null && ovr.hasAccess === true)) {
        return {
          hasAccess: true,
          source: 'override',
        };
      }
    }

    return {
      hasAccess: false,
      source: routePerm ? 'route' : 'default',
      deniedReason: `Requires ${minimumRole} role`,
    };
  }

  // A successful empty database result means that this reviewed source route
  // is authentication-only. Transport/query failures throw above and are
  // converted to a closed denial by the route guard.
  if (!routePerm) {
    return { hasAccess: true, source: 'route' };
  }

  // Check required permissions
  if (routePerm.required_permissions && routePerm.required_permissions.length > 0) {
    for (const [resource, action] of routePerm.required_permissions) {
      const hasRolePerm = await checkRolePermission(context.role, resource, action);
      if (!hasRolePerm) {
        return {
          hasAccess: false,
          source: 'route',
          deniedReason: `Missing permission: ${resource}:${action}`,
        };
      }
    }
  }

  // Check required features
  if (routePerm.required_features && routePerm.required_features.length > 0) {
    for (const featureKey of routePerm.required_features) {
      const featureAccess = await checkUnifiedAccess(userId, { feature: featureKey });
      if (!featureAccess.hasAccess) {
        return {
          hasAccess: false,
          source: featureAccess.source === 'override' ? 'override' : 'route',
          deniedReason:
            featureAccess.source === 'override' && featureAccess.deniedReason
              ? `${featureAccess.deniedReason} (${featureKey})`
              : `Missing feature access: ${featureKey}`,
        };
      }
    }
  }

  return { hasAccess: true, source: 'route' };
}

// ============================================
// Unified Permission Check
// ============================================

interface PermissionCheckContext {
  route?: string;
  feature?: string;
  resource?: string;
  action?: string;
}

/**
 * Main unified permission check function
 * Implements the permission resolution algorithm
 */
export async function checkUnifiedAccess(
  userId: string,
  context: PermissionCheckContext
): Promise<UnifiedPermission> {
  const userContext = await getUserContext(userId);

  if (!userContext) {
    return {
      hasAccess: false,
      source: 'default',
      deniedReason: 'User not found',
    };
  }

  if (!userContext.isApproved) {
    return {
      hasAccess: false,
      source: 'approval',
      deniedReason: 'User not approved',
    };
  }

  // Super admin bypasses all checks
  if (userContext.role === 'super_admin') {
    return { hasAccess: true, source: 'role' };
  }

  // 1. Check user-specific override (highest priority)
  if (context.feature) {
    const override = await checkUserOverride(userId, context.feature);
    if (override !== null) {
      return override;
    }
  }

  // 2. Check feature permission (from user_type)
  if (context.feature && userContext.userTypeId) {
    const featurePerm = await checkFeaturePermission(
      userContext.userTypeId,
      context.feature
    );
    if (featurePerm !== null) {
      return featurePerm;
    }
  }

  // 3. Check role permission
  if (context.resource && context.action) {
    const hasRolePerm = await checkRolePermission(
      userContext.role,
      context.resource,
      context.action
    );
    if (hasRolePerm) {
      return { hasAccess: true, source: 'role' };
    }
  }

  // 4. Check route permission
  if (context.route) {
    return checkRouteAccess(userId, context.route);
  }

  // 5. Default: DENY
  return {
    hasAccess: false,
    source: 'default',
    deniedReason: 'No explicit permission found',
  };
}

/**
 * Check user-specific override
 */
async function checkUserOverride(
  userId: string,
  featureKey: string
): Promise<UnifiedPermission | null> {
  try {
    const { data, error } = await supabase
      .from('user_permission_overrides')
      .select('can_access, config')
      .eq('user_id', userId)
      .eq('feature_key', featureKey)
      .or('expires_at.is.null,expires_at.gt.now()')
      .maybeSingle();

    if (error || !data) return null;
    if (data.can_access === null) return null;

    return {
      hasAccess: data.can_access,
      source: 'override',
      config: data.config || {},
      deniedReason: data.can_access ? undefined : 'Access explicitly denied',
    };
  } catch {
    return null;
  }
}

/**
 * Check feature permission from user_type
 */
async function checkFeaturePermission(
  userTypeId: string,
  featureKey: string
): Promise<UnifiedPermission | null> {
  try {
    const { data, error } = await supabase
      .from('feature_permissions')
      .select('can_access, config')
      .eq('user_type_id', userTypeId)
      .eq('feature_key', featureKey)
      .maybeSingle();

    if (error || !data) return null;

    return {
      hasAccess: data.can_access,
      source: 'user_type',
      config: data.config || {},
      deniedReason: data.can_access ? undefined : 'Feature not enabled for user type',
    };
  } catch {
    return null;
  }
}

// ============================================
// Security Audit Logging
// ============================================

/**
 * Log a security event to the audit log
 */
export async function logSecurityEvent(event: SecurityEvent): Promise<void> {
  try {
    await supabase.from('security_audit_log').insert({
      event_type: event.eventType,
      user_id: event.userId,
      details: event.details || {},
      severity: event.severity || 'warning',
      route_path: event.routePath,
      resource: event.resource,
      action: event.action,
    });
  } catch (error) {
    // Never fail on audit logging, but log to console
    console.error('[Security Audit] Failed to log event:', event, error);
  }
}

/**
 * Log an unauthorized access attempt
 */
export async function logUnauthorizedAccess(
  userId: string,
  details: {
    route?: string;
    feature?: string;
    resource?: string;
    action?: string;
    reason?: string;
  }
): Promise<void> {
  await logSecurityEvent({
    eventType: 'unauthorized_access_attempt',
    userId,
    details: {
      ...details,
      timestamp: new Date().toISOString(),
    },
    severity: 'warning',
    routePath: details.route,
    resource: details.resource,
    action: details.action,
  });
}

// ============================================
// Convenience Functions (backward compatible)
// ============================================

/**
 * Get current user's role (backward compatible)
 */
export async function getCurrentUserRole(): Promise<Role | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const context = await getUserContext(user.id);
    return context?.role || null;
  } catch {
    return null;
  }
}

/**
 * Check if current user has a specific role (or higher)
 */
export async function hasRole(requiredRole: Role): Promise<boolean> {
  const currentRole = await getCurrentUserRole();
  if (!currentRole) return false;

  const userLevel = ROLE_HIERARCHY[currentRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;

  return userLevel >= requiredLevel;
}

/**
 * Check if current user has permission for resource:action
 */
export async function hasPermission(resource: string, action: string): Promise<boolean> {
  const role = await getCurrentUserRole();
  if (!role) return false;

  return checkRolePermission(role, resource, action);
}

/**
 * Check if current user is admin or super_admin
 */
export async function isAdmin(): Promise<boolean> {
  return hasRole('admin');
}

/**
 * Check if current user is super_admin
 */
export async function isSuperAdmin(): Promise<boolean> {
  return hasRole('super_admin');
}

/**
 * Check if current user can access admin dashboards
 */
export async function canAccessAdminDashboards(): Promise<boolean> {
  return isAdmin();
}

/**
 * Granular Workbench & Action Element Permission Keys
 * Controllable per role and per individual team member via user_permission_overrides.
 */
export const GRANULAR_ELEMENT_PERMISSION_KEYS = [
  'card_vault.fop_copy',
  'card_vault.reveal',
  'payment_links.create',
  'rtw_desk.manage',
  'ticketing_queue.handover',
  'hotel_automation.configure',
  'flight_quote_research.use',
  'flight_quote_research.configure',
  'call_transcripts.search',
  'call_transcripts.backfill',
  'sabre_pool.view',
  'ops_command_center.view',
  'ops_command_center.reassign',
  'ops_command_center.routing_rules',
] as const;

export type GranularElementPermissionKey = (typeof GRANULAR_ELEMENT_PERMISSION_KEYS)[number];

/**
 * Check whether a user has permission for a granular workbench or action element.
 * Evaluates user_permission_overrides first (allow / deny), then user_type feature_permissions,
 * with super_admin bypass.
 */
export async function checkActionPermission(
  userId: string,
  featureKey: string
): Promise<UnifiedPermission> {
  return checkUnifiedAccess(userId, { feature: featureKey });
}

// ============================================
// Role Hierarchy Export
// ============================================

export { ROLE_HIERARCHY };

export default {
  // Core functions
  checkUnifiedAccess,
  checkRouteAccess,
  checkRolePermission,
  checkActionPermission,
  getUserContext,

  // Cache management
  clearUnifiedPermissionCache,
  clearGlobalPermissionCaches,

  // Data access
  getRolePermissions,
  getRoutePermissions,

  // Security
  logSecurityEvent,
  logUnauthorizedAccess,

  // Backward compatible
  getCurrentUserRole,
  hasRole,
  hasPermission,
  isAdmin,
  isSuperAdmin,
  canAccessAdminDashboards,

  // Constants
  ROLE_HIERARCHY,
  GRANULAR_ELEMENT_PERMISSION_KEYS,
};

